import * as React from 'react'
import {
  Card,
  CardContent,
  Rule,
  TextField,
  Heading,
  Button,
  Markdown,
  Paragraph,
  InputGroup,
  StyledText,
} from '@dooer/react-ui-kit'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import messages from './messages'
import styles from './styles'
import Numbers from '../numbers'
import Success from '../success'
import { css, Rule as GlamorRule } from 'glamor'
import { FeedbackQuestion, FeedbackResult } from '../../types'

interface IBasicProps {
  question?: FeedbackQuestion
  results?: {
    feedbackResults: {
      edges: {
        node: FeedbackResult
      }[]
    }
  }
  onSubmit?: (newScore: number, newComment?: string | null) => Promise<void>
  style?: GlamorRule
  loading?: boolean
  includeColors?: boolean
  textAreaId?: string
  initialScore?: number | null
  initialComment?: string | null
}

export const Basic: React.FC<IBasicProps> = ({
  question,
  results,
  onSubmit,
  style,
  loading,
  includeColors,
  textAreaId,
  initialScore,
  initialComment,
}) => {
  // Frequently used variable
  const answer = question && results && results?.feedbackResults?.edges[0]?.node

  // State management
  const [errors, setErrors] = React.useState<{ [key: string]: MessageDescriptor }>({})
  const [submitted, toggleSubmitted] = React.useState<boolean>()
  const [chosenScore, setChosenScore] = React.useState<number | undefined | null>(initialScore)
  const [chosenComment, setChosenComment] = React.useState<string | undefined | null>(initialComment)

  const isScoreValid = typeof chosenScore === 'number' && chosenScore >= 0 && chosenScore <= 10

  // Submit form
  const handleSubmit = async () => {
    const validationErrors = {
      ...(!isScoreValid && { score: messages.alert }),
    }

    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0 && onSubmit && typeof chosenScore === 'number') {
      onSubmit(chosenScore, chosenComment).then(() => {
        toggleSubmitted(true)
      })
    }
  }

  React.useEffect(() => {
    if (answer) {
      setChosenScore(answer.score)
      setChosenComment(answer.feedback)
    }
  }, [answer])

  return (
    <Card style={[style, styles.root]}>
      <CardContent style={styles.content}>
        {submitted ? (
          <Success />
        ) : (
          <>
            {question && question.description ? (
              <Markdown source={question.description} />
            ) : (
              <Heading>
                <FormattedMessage {...messages.heading} />
              </Heading>
            )}

            <i>
              <Paragraph>
                <FormattedMessage {...messages.instructions} />
              </Paragraph>
            </i>

            <Rule />

            <Numbers
              amount={10}
              includeColors={includeColors}
              labelBad={question && question.labelBad}
              labelGood={question && question.labelGood}
              onChange={(value: number) => setChosenScore(value)}
              value={chosenScore}
            />

            {errors.score ? (
              <StyledText style={styles.error} variant="negative">
                <FormattedMessage {...errors.score} />
              </StyledText>
            ) : null}

            <Rule />

            <InputGroup>
              <label htmlFor={textAreaId}>
                <Heading level="h2" {...css(styles.feedbackHeading)}>
                  <FormattedMessage {...messages.feedbackHeading} />
                </Heading>
              </label>
              <TextField
                id={textAreaId}
                multiline
                onChange={e => setChosenComment(e.target.value)}
                placeholder="Kommentar"
                value={chosenComment || ''}
                {...css(styles.textarea)}
              />
            </InputGroup>

            <Button
              block
              disableOnProcessing
              flush
              onClick={handleSubmit}
              processing={loading}
              variant="brand"
              {...css(styles.cta)}
            >
              <FormattedMessage {...messages.cta} />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
